import paths from "common/services/paths-service";
import { coreApiSlice } from "../../core-api-slice";

const updateCampaignGroupClients = async (
  { campaignGroupId },
  { dispatch, queryFulfilled, getState }
) => {
  const invalidCachedQueries = coreApiSlice.util.selectInvalidatedBy(getState(), [
    { type: "Campaign", id: campaignGroupId }
  ]);

  const { data: updatedCampaignGroup } = await queryFulfilled;
  // wait for the mutation to complete; then update the cache
  invalidCachedQueries.forEach(({ endpointName, originalArgs }) => {
    dispatch(
      coreApiSlice.util.updateQueryData(endpointName, originalArgs, draft => {
        if (draft.campaignGroups) {
          const campaignGroup = draft.campaignGroups.find(cg => cg.id === campaignGroupId);
          if (campaignGroup) {
            campaignGroup.clients = updatedCampaignGroup.clients;
          }
        } else if (draft.campaignGroup?.id === campaignGroupId) {
          draft.campaignGroup.clients = updatedCampaignGroup.clients;
        }
      })
    );
  });
};

// Replaces the cached campaign group with the one the server just re-serialized.
const updateCachedCampaignGroup = async (
  { campaignGroupId },
  { dispatch, queryFulfilled, getState }
) => {
  const invalidCachedQueries = coreApiSlice.util.selectInvalidatedBy(getState(), [
    { type: "Campaign", id: campaignGroupId }
  ]);

  let updatedCampaignGroupResponse;
  try {
    ({ data: updatedCampaignGroupResponse } = await queryFulfilled);
  } catch {
    // The mutation failed, so the cached group is still the accurate one. Awaiting
    return;
  }

  // Preserve the cached campaign group when Stripe 3DS requires additional action,
  if (updatedCampaignGroupResponse.requiresAction === true) return;

  // wait for the mutation to complete; then update the cache
  invalidCachedQueries.forEach(({ endpointName, originalArgs }) => {
    dispatch(
      coreApiSlice.util.updateQueryData(endpointName, originalArgs, draft => {
        if (draft.campaignGroups) {
          const index = draft.campaignGroups.findIndex(cg => cg.id === campaignGroupId);
          if (index !== -1) {
            // eslint-disable-next-line no-param-reassign
            draft.campaignGroups[index] = updatedCampaignGroupResponse.campaign_group;
          }
        } else if (draft.campaignGroup?.id === campaignGroupId) {
          // eslint-disable-next-line no-param-reassign
          draft.campaignGroup = updatedCampaignGroupResponse.campaign_group;
        }
        if (updatedCampaignGroupResponse.campaign_package) {
          // eslint-disable-next-line no-param-reassign
          draft.campaignPackage = updatedCampaignGroupResponse.campaign_package;
        }
      })
    );
  });
};

const campaignsApiEndpoints = coreApiSlice.injectEndpoints({
  endpoints: build => ({
    getCampaigns: build.query({
      query: ({ accountId, filterBy, sortBy, searchString, perPage, page }) => {
        const url = paths.build(paths.filterAccountCampaignsPath, { accountId });
        return {
          url,
          params: {
            filter_by: filterBy,
            sort_by: sortBy,
            search_string: searchString,
            per_page: perPage,
            page
          }
        };
      },
      transformResponse: response => {
        return {
          campaignGroups: response.campaign_groups,
          totalCount: response.total_count,
          recordCount: response.record_count,
          images: response.images
        };
      },
      providesTags: result =>
        result
          ? [...result.campaignGroups.map(({ id }) => ({ type: "Campaign", id })), "Campaign"]
          : ["Campaign"]
    }),
    deleteCampaign: build.mutation({
      query: deleteDraftUrl => ({
        url: deleteDraftUrl,
        method: "DELETE"
      }),
      invalidatesTags: (_result, error) => {
        return !error ? [{ type: "Campaign" }] : [];
      }
    }),
    editCampaignName: build.mutation({
      query: ({ campaignTypeSnake, name, editCampaignUrl }) => ({
        url: editCampaignUrl,
        method: "POST",
        body: { [campaignTypeSnake]: { name } }
      }),
      async onQueryStarted({ name, campaignGroupId }, { dispatch, queryFulfilled, getState }) {
        const invalidCachedQueries = coreApiSlice.util.selectInvalidatedBy(getState(), [
          { type: "Campaign", id: campaignGroupId }
        ]);

        const updateResults = invalidCachedQueries.map(({ endpointName, originalArgs }) => {
          return dispatch(
            coreApiSlice.util.updateQueryData(endpointName, originalArgs, draft => {
              const campaignGroup = draft.campaignGroups.find(cg => cg.id === campaignGroupId);
              if (campaignGroup) {
                campaignGroup.name = name;
              }
            })
          );
        });

        queryFulfilled.catch(() => {
          // revert all updates if the mutation fails
          updateResults.forEach(p => p.undo());
        });
      }
    }),
    addCampaignClient: build.mutation({
      query: ({ client, addClientUrl }) => ({
        url: addClientUrl,
        method: "POST",
        body: {
          campaign_client: {
            full_name: client.full_name,
            email: client.email,
            agent_name: client.agent_name
          },
          account: {
            send_a_copy: client.send_a_copy
          }
        }
      }),
      onQueryStarted: updateCampaignGroupClients
    }),
    editCampaignClient: build.mutation({
      query: ({ client, editClientUrl }) => ({
        url: editClientUrl,
        method: "POST",
        body: {
          campaign_client: {
            full_name: client.full_name,
            email: client.email
          },
          client_id: client.id
        }
      }),
      onQueryStarted: updateCampaignGroupClients
    }),
    deleteCampaignClient: build.mutation({
      query: ({ clientId, deleteClientUrl }) => ({
        url: deleteClientUrl,
        method: "POST",
        body: {
          client_id: clientId
        }
      }),
      onQueryStarted: updateCampaignGroupClients
    }),
    submitPurchase: build.mutation({
      query: ({ params, purchaseUrl }) => ({
        url: purchaseUrl,
        method: "POST",
        body: params
      }),
      onQueryStarted: updateCachedCampaignGroup
    }),
    applyCouponCode: build.mutation({
      query: ({ params, applyCouponUrl }) => ({
        url: applyCouponUrl,
        method: "POST",
        dataType: "json",
        body: params
      })
    }),
    getCampaignResults: build.query({
      query: ({ accountId, campaignId, cancelAuthorization }) => ({
        url: paths.build(paths.accountCampaignResultsPath, { accountId, campaignId }),
        params: cancelAuthorization ? { cancel_authorization: cancelAuthorization } : undefined
      }),
      transformResponse: response => response,
      providesTags: result => (result ? [{ type: "Campaign", id: result.campaignGroup.id }] : [])
    }),
    getCampaignPerformance: build.query({
      query: ({ accountId, campaignId, fromDate, toDate, clientToken }) => ({
        url: paths.build(paths.accountCampaignPerformancePath, { accountId, campaignId }),
        params: {
          ...(fromDate && toDate ? { dateRange: { from: fromDate, to: toDate } } : {}),
          ...(clientToken ? { client_token: clientToken } : {})
        }
      }),
      transformResponse: response => response
    }),
    changeCampaignPayment: build.mutation({
      query: ({ saveUrl, paymentDetails, applyToAllActiveCampaigns, stripeFields = {} }) => ({
        url: saveUrl,
        method: "POST",
        body: {
          credit_card_number: paymentDetails.creditCardNumber,
          credit_card_expiration_month: paymentDetails.expirationMonth,
          credit_card_expiration_year: paymentDetails.expirationYear,
          credit_card_cvv: paymentDetails.cvv,
          billing_zip: paymentDetails.zipCode,
          transaction_id: paymentDetails.transactionId,
          apply_to_all_active_campaigns: applyToAllActiveCampaigns,
          ...stripeFields
        }
      })
    }),
    initializeCancellationFlow: build.query({
      query: url => ({ url })
    }),
    submitCancelFeedback: build.mutation({
      query: ({ url, feedbackOption, otherText }) => ({
        url,
        method: "POST",
        body: {
          cancellation_flow: { cancel_feedback_option: feedbackOption },
          ...(otherText ? { other_text: otherText } : {})
        }
      })
    }),
    submitCancelOption: build.mutation({
      query: ({ url, cancelOption }) => ({
        url,
        method: "POST",
        body: { cancellation_flow: { cancel_option: cancelOption } }
      }),
      invalidatesTags: (_result, error, _args) => (!error ? [{ type: "Campaign" }] : [])
    }),
    submitZipCodeRelease: build.mutation({
      query: ({ url, fullName, agentRelationship }) => ({
        url,
        method: "POST",
        body: {
          zip_code_release: { full_name: fullName, agent_relationship: agentRelationship, terms: 1 }
        }
      }),
      invalidatesTags: (_result, error) => (!error ? [{ type: "Campaign" }] : [])
    }),
    reenableRenewal: build.mutation({
      query: ({ url }) => ({ url, method: "POST" }),
      invalidatesTags: (_result, error) => (!error ? [{ type: "Campaign" }] : [])
    }),
    updateDestinationPage: build.mutation({
      query: ({ saveUrl, url }) => ({
        url: saveUrl,
        method: "POST",
        body: { url }
      }),
      async onQueryStarted({ accountId, campaignId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            coreApiSlice.util.updateQueryData(
              "getCampaignResults",
              { accountId, campaignId },
              draft => {
                // eslint-disable-next-line no-param-reassign
                draft.destinationURL = data.landingPage;
              }
            )
          );
        } catch {} // eslint-disable-line no-empty
      }
    })
  })
});

// eslint-disable-next-line import/prefer-default-export
export const {
  useGetCampaignsQuery,
  useDeleteCampaignMutation,
  useEditCampaignNameMutation,
  useAddCampaignClientMutation,
  useEditCampaignClientMutation,
  useDeleteCampaignClientMutation,
  useSubmitPurchaseMutation,
  useApplyCouponCodeMutation,
  useGetCampaignResultsQuery,
  useGetCampaignPerformanceQuery,
  useChangeCampaignPaymentMutation,
  useUpdateDestinationPageMutation,
  useInitializeCancellationFlowQuery,
  useLazyInitializeCancellationFlowQuery,
  useSubmitCancelFeedbackMutation,
  useSubmitCancelOptionMutation,
  useSubmitZipCodeReleaseMutation,
  useReenableRenewalMutation
} = campaignsApiEndpoints;
